import { watch } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import store from '@/store'
import { v2ReservationGroupKeys } from '@/composables/queries/v2reservationgroup/use-v2-reservationgroup-find-query'
import { v2ReservationGroupRemovedKeys } from '@/composables/queries/v2reservationgroup/use-v2-reservationgroup-find-removed-query'
import { v2ReservationColumnRemovedKeys } from '@/composables/queries/v2reservationcolumn/use-v2-reservationcolumn-find-removed-query'
import { reservationColumnKeys } from '@/composables/queries/reservationcolumn/use-reservation-column-find-query'
import { v2ReservationStatusKeys } from '@/composables/queries/v2reservationstatus/use-v2-reservationstatus-find-query'
import { v2ReservationProgramKeys } from '@/composables/queries/v2reservationprogram/use-v2-reservationprogram-find-query'
import { v2ReservationSettingKeys } from '@/composables/queries/v2reservationsetting/use-v2-reservationsetting-find-one-query'
import { v2ReservationListKeys } from '@/composables/queries/v2reservation/use-v2-reservation-list-query'
import { v2ReservationCellKeys } from '@/composables/queries/v2reservationcell/use-v2-reservationcell-find-query'
import { v2ReservationBlockKeys } from '@/composables/queries/v2reservationblock/use-v2-reservationblock-find-query'
import { v2WorkRecordDateKeys } from '@/composables/queries/v2workrecord/use-v2-workrecord-find-date-query'
import { v2WorkHolidayDateKeys } from '@/composables/queries/v2workholiday/use-v2-workholiday-find-date-query'
import { statsReservationDailyKeys } from '@/composables/queries/stats/use-stats-v2reservation-daily-find-query'
import { v2HReservationKeys } from '@/composables/queries/v2hreservation/use-v2-hreservation-find-query'
import { v2HReservationCountKeys } from '@/composables/queries/v2hreservation/use-v2-hreservation-count-query'

const GROUP_KEYS = [v2ReservationGroupKeys.all, v2ReservationGroupRemovedKeys.all]
const COLUMN_KEYS = [reservationColumnKeys.all, v2ReservationColumnRemovedKeys.all]
const RESERVATION_KEYS = [
  v2ReservationListKeys.all,
  statsReservationDailyKeys.all,
  v2HReservationKeys.all,
  v2HReservationCountKeys.all,
]

/*
 * FCM 푸시 코드 → 무효화할 쿼리 키 매핑.
 * 다른 사용자의 변경이 rm store(remote_notification)로 들어오면 해당 도메인 캐시를
 * stale 처리해, staleTime 이 길어도 다음 조회가 fresh 데이터를 가져오게 한다.
 * (staleTime Infinity 도메인 — group·column·setting — 은 이 매핑이 유일한 갱신 트리거다)
 */
const PUSH_INVALIDATION_MAP: Record<string, readonly (readonly unknown[])[]> = {
  d_v2ReservationGroupCreate_el: GROUP_KEYS,
  d_v2ReservationGroupUpdate_el: GROUP_KEYS,
  // 그룹 삭제는 소속 컬럼도 함께 제거되므로 컬럼 캐시도 무효화
  d_v2ReservationGroupRemove_el: [...GROUP_KEYS, ...COLUMN_KEYS],
  d_v2ReservationGroupUpdateSort_el: GROUP_KEYS,

  d_v2ReservationColumnCreate_el: COLUMN_KEYS,
  d_v2ReservationColumnUpdate_el: COLUMN_KEYS,
  d_v2ReservationColumnRemove_el: COLUMN_KEYS,
  d_v2ReservationColumnUpdateSort_el: COLUMN_KEYS,

  d_v2ReservationStatusUpdateSort_el: [v2ReservationStatusKeys.all],
  // 컬럼 문서가 programs 정렬을 포함하므로 프로그램 정렬 변경 시 컬럼 캐시도 무효화
  d_v2ReservationProgramUpdateSort_el: [v2ReservationProgramKeys.all, ...COLUMN_KEYS],

  d_v2ReservationSettingUpdate_el: [v2ReservationSettingKeys.all],
}

/*
 * 날짜별 데이터(예약·셀·블록·근무기록·휴무) 푸시 매핑.
 * 재조회 트리거는 기존 흐름(ReservationSheet 워처 → handleRefresh/refresh 이벤트)이 담당하므로
 * 여기서는 stale 마킹만 한다 (refetchType 'none' — 이중 재조회 방지).
 */
const PUSH_STALE_ONLY_MAP: Record<string, readonly (readonly unknown[])[]> = {
  d_v2ReservationCreate_el: RESERVATION_KEYS,
  d_v2ReservationUpdate_el: RESERVATION_KEYS,
  d_v2ReservationCancel_el: RESERVATION_KEYS,
  d_v2ReservationRemove_el: RESERVATION_KEYS,

  d_v2ReservationCellCreate_el: [v2ReservationCellKeys.all],
  d_v2ReservationCellUpdate_el: [v2ReservationCellKeys.all],
  d_v2ReservationCellRemove_el: [v2ReservationCellKeys.all],

  d_v2ReservationBlockCreate_el: [v2ReservationBlockKeys.all],
  d_v2ReservationBlockUpdate_el: [v2ReservationBlockKeys.all],
  d_v2ReservationBlockRemove_el: [v2ReservationBlockKeys.all],

  d_v2WorkRecordUpdateBatch_el: [v2WorkRecordDateKeys.all],
  d_v2WorkHolidayCreate_el: [v2WorkHolidayDateKeys.all],
  d_v2WorkHolidayRemove_el: [v2WorkHolidayDateKeys.all],
}

/** 예약시트 루트(setup)에서 1회 장착한다. */
export function usePushQueryInvalidation() {
  const queryClient = useQueryClient()
  const register = (map: Record<string, readonly (readonly unknown[])[]>, refetchType?: 'none') => {
    for (const [code, keyList] of Object.entries(map)) {
      watch(
        () => (store.state as Record<string, Record<string, unknown>>).remote_notification[code],
        (value) => {
          if (!value) return
          for (const queryKey of keyList) {
            queryClient.invalidateQueries({ queryKey, refetchType })
          }
        },
      )
    }
  }
  register(PUSH_INVALIDATION_MAP)
  register(PUSH_STALE_ONLY_MAP, 'none')
}
